import React from 'react';
import { SVGClock } from '../assets/PackageSVG';
import {
  AirlineLogo,
  formatDuration,
  formatMoney,
  formatTime,
  stopsLabel,
} from './flightPresentation';
import styles from './FlightCardWidget.module.scss';

/** Marketing/operating carrier, as the flights-details playbook's
 * `_airline()` builds it — real name plus Duffel's own logo URLs. */
export type FlightAirline = {
  iata_code?: string;
  name?: string;
  logo_symbol_url?: string;
  logo_lockup_url?: string;
};

/** One entry from `baggage_options[]`. `scope: 'included'` is what the fare
 * already grants (no extra API call needed); `scope: 'available'` is a
 * priced add-on that only appears when the search ran with
 * `enrich_offers` on. */
export type FlightBaggageOption = {
  scope: 'included' | 'available';
  type: string;
  quantity: number;
  amount?: string | null;
  currency?: string | null;
  service_id?: string | null;
  max_weight_kg?: number | null;
  segment_ids?: string[];
  passenger_ids?: string[];
};

/** One entry from `conditions_options[]`. `allowed: null` means the fare
 * doesn't state the term — unknown, not "not allowed". */
export type FlightConditionOption = {
  scope: 'offer' | 'slice';
  type: string;
  key?: string;
  allowed: boolean | null;
  penalty_amount?: string | null;
  penalty_currency?: string | null;
  slice_id?: string;
  slice_direction?: string;
};

type FlightSegmentEndpoint = {
  iata: string;
  at: string;
  /** Present when the gateway supplies it; components fall back to a
   * local lookup keyed by `iata` when absent. */
  city?: string;
  airport_name?: string;
  terminal?: string;
};

export type FlightCardPayload = {
  offer_id: string;
  price: {
    total: string;
    currency: string;
  };
  itineraries: {
    duration?: string;
    direction?: string;
    origin?: FlightSegmentEndpoint;
    destination?: FlightSegmentEndpoint;
    /** Not a field the API sends per-itinerary -- price is offer-level.
     * Present when a caller (Chat.tsx's round-trip merge) has stamped this
     * leg's own offer price onto it, since `FlightCardPayload.price` on a
     * merged round-trip booking is the outbound+return COMBINED total. */
    price?: { total: string; currency: string };
    segments: {
      departure: FlightSegmentEndpoint;
      arrival: FlightSegmentEndpoint;
      carrier: string;
      carrier_name?: string;
      airline?: FlightAirline;
      operating_carrier?: string;
      operating_carrier_name?: string;
      operating_airline?: FlightAirline;
      flight_number?: string;
      duration: string;
      stops?: number;
    }[];
    /** Baggage/condition/fare-brand data is offer-level in the API (one
     * set per direction), not itinerary-level — a caller merging an
     * outbound + return offer into one booking is expected to copy each
     * offer's own fields onto its leg's itinerary before merging, which is
     * exactly what Chat.tsx's round-trip merge does. */
    baggage_options?: FlightBaggageOption[];
    baggage_known?: boolean;
    conditions_options?: FlightConditionOption[];
    conditions_known?: boolean;
    fare_brand?: string | null;
  }[];
  carriers: string[];
  duration: string;
  stops: number;
  direction?: string;
  validating_airline?: string;
  ctas?: ('view_details' | 'book_this')[];
  ai_adjustments?: { emphasis?: string };
  /** Validating/headline carrier for the whole offer. */
  airline?: FlightAirline;
  /** Every carrier flying any leg of the offer, deduplicated. */
  airlines?: FlightAirline[];
  baggage_options?: FlightBaggageOption[];
  baggage_known?: boolean;
  conditions_options?: FlightConditionOption[];
  conditions_known?: boolean;
  fare_brand?: string | null;
};

export function FlightCardWidget({
  payload,
  variant = 'compact',
  onAction,
  selected = false,
  onSelect,
  showView = true,
  viewDisabled = false,
  viewDisabledReason,
}: {
  payload: FlightCardPayload;
  variant?: 'compact' | 'in_popover' | 'full';
  onAction?: (actionId: string, value?: Record<string, unknown>) => void;
  /** Highlights the whole card as the current pick. */
  selected?: boolean;
  /** Makes the whole card clickable; called with this card's payload. */
  onSelect?: (payload: FlightCardPayload) => void;
  /** Renders the blue View button. False leaves a select-only card (the
   * outbound side of a round trip carries no buttons at all). */
  showView?: boolean;
  viewDisabled?: boolean;
  viewDisabledReason?: string;
}) {
  const segments = payload.itineraries[0]?.segments || [];
  const first = segments[0];
  const last = segments[segments.length - 1] || first;
  const isCompact = variant === 'compact' || variant === 'in_popover';
  const emphasized = payload.ai_adjustments?.emphasis === 'cheapest';

  const carrier = payload.carriers?.[0] || first?.carrier || payload.validating_airline;
  const route = `${first?.departure.iata || '---'} → ${last?.arrival.iata || '---'}`;

  const canView = showView && (!payload.ctas || payload.ctas.includes('view_details'));

  return (
    <article
      className={[
        styles.card,
        emphasized && styles.emphasized,
        onSelect && styles.selectable,
        selected && styles.selected,
      ].filter(Boolean).join(' ')}
      role={onSelect ? 'option' : undefined}
      aria-selected={onSelect ? selected : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(payload) : undefined}
      onKeyDown={onSelect ? (e) => {
        // Only the card itself -- Enter/Space on the View button inside it
        // is that button's own to handle.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(payload);
        }
      } : undefined}
    >
      {/* Airline */}
      <div className={styles.airline}>
        <AirlineLogo code={carrier} name={carrier} />
        <span className={styles.carrier} title={carrier}>
          {carrier || 'Airline'}
        </span>
      </div>

      {/* Times + trip meta */}
      <div className={styles.trip}>
        <div className={styles.times}>
          <span>{formatTime(first?.departure.at)}</span>
          <span className={styles.dash}>–</span>
          <span>{formatTime(last?.arrival.at)}</span>
        </div>

        <div className={styles.meta}>
          <span className={styles.metaItem}>
            <SVGClock className={styles.metaIcon} aria-hidden="true" />
            {formatDuration(payload.itineraries[0]?.duration || payload.duration)}
          </span>
          <span className={styles.metaSep} aria-hidden="true" />
          <span className={styles.metaItem}>{route}</span>
          <span className={styles.metaSep} aria-hidden="true" />
          <span className={styles.metaItem}>
            Stop:&nbsp;<span className={styles.stops}>{stopsLabel(payload.stops)}</span>
          </span>
        </div>

        {/* Every leg, for the non-compact variant */}
        {!isCompact && segments.length > 1 ? (
          <div className={styles.segments}>
            {payload.itineraries
              .flatMap((itin) => itin.segments)
              .map((seg, idx) => (
                <span key={`${seg.carrier}-${seg.flight_number}-${idx}`} className={styles.segmentLine}>
                  {seg.departure.iata} → {seg.arrival.iata} · {formatDuration(seg.duration)}
                </span>
              ))}
          </div>
        ) : null}
      </div>

      {/* Price + CTA */}
      <div className={styles.priceBlock}>
        <span className={styles.amount}>
          {formatMoney(payload.price?.total, payload.price?.currency) || 'Price pending'}
        </span>
        {canView ? (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={viewDisabled}
              title={viewDisabled ? viewDisabledReason : undefined}
              onClick={() => onAction?.(`view_offer:${payload.offer_id}`, {
                offer_id: payload.offer_id,
                offer: payload,
              })}
            >
              View
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}
